import type { ReduxSagaModel } from './types';
import { logger } from '@/utils/logger';

export type SpotlightContext = 'queryEditor' | 'conversation' | 'project' | null;

export type State = {
  isMarketPlaceUiOpen: boolean;
  isSpotlightOpen: boolean;
  isConnectorChooserOpen: boolean;
  spotlightContext: SpotlightContext;
  // Summonable AI Insights panel (right-side, PUSHES/compresses page content). Toggled via the
  // generic `overlay/update` effect (overlayType: 'isAiPanelOpen').
  isAiPanelOpen: boolean;
  // Current panel width, mirrored here (the panel keeps a local copy + localStorage) so the layout
  // can reserve exactly this much space on the right — the page content is padded by it rather than
  // overlaid. Updated on open and after a resize-drag settles (not per mousemove).
  aiPanelWidth: number;
  // Columns of persisted dataset-view filters/sortings that were dropped on
  // restore because they no longer exist in the dataset. `null` means nothing
  // to report; a non-empty array triggers a one-time notification modal.
  removedViewStateColumns: string[] | null;
};

interface SaveUpdateAction {
  type: 'saveUpdate';
  payload: { [record: string]: boolean | SpotlightContext };
}

interface SetAiPanelWidthAction {
  type: 'setAiPanelWidth';
  payload: { width: number };
}

export type SaveRemovedViewStateColumnsAction = {
  type: 'overlay/saveRemovedViewStateColumns' | 'saveRemovedViewStateColumns';
  payload: Pick<State, 'removedViewStateColumns'>;
};

const initialState: State = {
  isMarketPlaceUiOpen: false,
  isSpotlightOpen: false,
  isConnectorChooserOpen: false,
  spotlightContext: null,
  isAiPanelOpen: false,
  aiPanelWidth: 480,
  removedViewStateColumns: null,
};

const OverlayModel: ReduxSagaModel<
  State,
  {
    saveUpdate: SaveUpdateAction;
    setAiPanelWidth: SetAiPanelWidthAction;
    saveRemovedViewStateColumns: SaveRemovedViewStateColumnsAction;
  }
> = {
  namespace: 'overlay',
  state: initialState,
  effects: {
    *update({ payload }: { payload: { overlayType: string; status: boolean; context?: SpotlightContext } }, { put }) {
      logger.debug({ label: 'app.models.overlay.update', message: payload });
      const { overlayType, status, context } = payload;

      const updatePayload: { [record: string]: boolean | SpotlightContext } = { [overlayType]: status };

      // If opening spotlight with context, store it; if closing, reset it
      if (overlayType === 'isSpotlightOpen') {
        updatePayload.spotlightContext = status ? (context ?? null) : null;
      }

      yield put<SaveUpdateAction>({
        type: 'saveUpdate',
        payload: updatePayload,
      });
    },
  },
  reducers: {
    resetAll() {
      return { ...initialState };
    },
    saveUpdate(state, { payload }) {
      return { ...state, ...payload };
    },
    setAiPanelWidth(state, { payload }: SetAiPanelWidthAction) {
      return { ...state, aiPanelWidth: payload.width };
    },
    saveRemovedViewStateColumns(state, { payload }) {
      return { ...state, ...payload };
    },
  },
};

export default OverlayModel;
